interface RegistrationFormData {
  fullName: string;
  age: number;
  contactNumber: string;
  email: string;
  attendanceDays: ('day1' | 'day2')[];
  isFeastMagnolia: boolean;
  otherFeast?: string;
  howDidYouLearn: 'social_media' | 'feast' | 'friend';
  friendName?: string;
  bringingKids: boolean;
  kidsCount?: number;
  kidsDetails?: string;
}

interface RegistrationResponse {
  success: boolean;
  message: string;
  participantId?: string;
  checkInPin?: string;
  error?: string;
}

interface ValidationResult {
  isValid: boolean;
  errors: { field: string; message: string; code: string }[];
}

import type { Participant } from '@/types';


interface RegistrationStatusResponse {
  success: boolean;
  total: number;
  verified: number;
  pending: number;
  attended: number;
  remaining: number;
  maxCapacity: number;
  isOpen: boolean;
}

interface ParticipantLookupResponse {
  success: boolean;
  participant?: Participant;
  error?: string;
}

const EVENT_CONSTANTS = {
  MAX_CAPACITY: 1000,
  REGISTRATION_DEADLINE: '2026-03-29T23:59:59+08:00',
  MIN_AGE: 13,
  MAX_AGE: 100,
  PIN_LENGTH: 4,
} as const;

const APPS_SCRIPT_URL =
  import.meta.env.VITE_APPS_SCRIPT_URL ||
  'https://script.google.com/macros/s/AKfycbwGrM7-Gdl_sWWlj1TTYM-7mU3nA_hsqMsXDCGYTF2sSVMo37tv7zSsOxaQdJ917sRF/exec';

/**
 * Validate registration form data
 */
export const validateRegistrationData = (data: RegistrationFormData): ValidationResult => {
  const errors = [];

  // Name validation
  if (!data.fullName || data.fullName.trim().length < 2) {
    errors.push({
      field: 'fullName',
      message: 'Full name must be at least 2 characters',
      code: 'INVALID_NAME',
    });
  }

  if (data.fullName.trim().length > 100) {
    errors.push({
      field: 'fullName',
      message: 'Full name is too long (max 100 characters)',
      code: 'NAME_TOO_LONG',
    });
  }

  // Email validation
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  if (!data.email || !emailRegex.test(data.email)) {
    errors.push({
      field: 'email',
      message: 'Please enter a valid email address',
      code: 'INVALID_EMAIL',
    });
  }

  // Age validation
  if (!data.age || data.age < EVENT_CONSTANTS.MIN_AGE || data.age > EVENT_CONSTANTS.MAX_AGE) {
    errors.push({
      field: 'age',
      message: `Age must be between ${EVENT_CONSTANTS.MIN_AGE} and ${EVENT_CONSTANTS.MAX_AGE}`,
      code: 'INVALID_AGE',
    });
  }

  // Contact number validation
  if (!data.contactNumber || data.contactNumber.trim().length < 10) {
    errors.push({
      field: 'contactNumber',
      message: 'Please enter a valid contact number',
      code: 'INVALID_CONTACT',
    });
  }

  // Attendance days validation
  if (!data.attendanceDays || data.attendanceDays.length === 0) {
    errors.push({
      field: 'attendanceDays',
      message: 'Please select at least one day',
      code: 'NO_DAYS_SELECTED',
    });
  }

  return {
    isValid: errors.length === 0,
    errors,
  };
};

/**
 * Get registration status from Apps Script
 */
export const getRegistrationStatus = async (): Promise<RegistrationStatusResponse | null> => {
  try {
    const response = await fetch(`${APPS_SCRIPT_URL}?action=status`);
    const result: RegistrationStatusResponse = await response.json();
    return result;
  } catch (error) {
    console.error('Error fetching registration status:', error);
    return null;
  }
};

/**
 * Get participant data from Apps Script
 */
export const getParticipantFromAppsScript = async (
  participantId: string
): Promise<Participant | null> => {
  try {
    const response = await fetch(
      `${APPS_SCRIPT_URL}?action=participant&id=${participantId}`
    );
    const result: ParticipantLookupResponse = await response.json();

    if (result.success && result.participant) {
      return result.participant;
    }
    return null;
  } catch (error) {
    console.error('Error fetching participant:', error);
    return null;
  }
};

/**
 * Register participant via Google Apps Script
 */
export const registerParticipant = async (
  formData: RegistrationFormData
): Promise<RegistrationResponse> => {
  try {
    // Validate form data first
    const validation = validateRegistrationData(formData);
    if (!validation.isValid) {
      return {
        success: false,
        message: validation.errors[0]?.message || 'Invalid registration data',
        error: validation.errors[0]?.code || 'VALIDATION_ERROR',
      };
    }

    // Send to Google Apps Script using FormData to avoid CORS issues
    const payload = {
      action: 'register',
      fullName: formData.fullName,
      email: formData.email,
      age: formData.age,
      contactNumber: formData.contactNumber,
      attendanceDays: formData.attendanceDays,
      isFeastMagnolia: formData.isFeastMagnolia,
      otherFeast: formData.otherFeast || '',
      howDidYouLearn: formData.howDidYouLearn,
      friendName: formData.friendName || '',
      bringingKids: formData.bringingKids,
      kidsCount: formData.kidsCount || 0,
      kidsDetails: formData.kidsDetails || '',
    };

    const form = new FormData();
    form.append('data', JSON.stringify(payload));

    const response = await fetch(APPS_SCRIPT_URL, {
      method: 'POST',
      body: form,
    });

    const result = await response.json();

    if (result.success) {
      return {
        success: true,
        message: result.message || 'Registration successful! Check your email.',
        participantId: result.participantId,
      };
    } else {
      return {
        success: false,
        message: result.message || 'Registration failed. Please try again.',
        error: result.error || 'REGISTRATION_ERROR',
      };
    }
  } catch (error) {
    console.error('Registration error:', error);
    return {
      success: false,
      message: 'Network error. Please check your connection and try again.',
      error: 'NETWORK_ERROR',
    };
  }
};

/**
 * Check registration availability
 */
export const checkRegistrationAvailability = async (): Promise<{
  isAvailable: boolean;
  reason?: string;
  remaining?: number;
}> => {
  try {
    // First check local deadline
    const now = new Date();
    const deadline = new Date(EVENT_CONSTANTS.REGISTRATION_DEADLINE);

    if (now > deadline) {
      return {
        isAvailable: false,
        reason: 'Registration deadline has passed (March 29, 2026 - 11:59 PM)',
      };
    }

    // Then check with Apps Script for capacity
    const status = await getRegistrationStatus();
    if (status) {
      if (!status.isOpen) {
        return {
          isAvailable: false,
          reason: status.remaining <= 0
            ? 'Registration is full. Please contact us for waitlist options.'
            : 'Registration is currently closed.',
          remaining: status.remaining,
        };
      }
      return { isAvailable: true, remaining: status.remaining };
    }

    return { isAvailable: true };
  } catch (error) {
    console.error('Error checking registration availability:', error);
    return { isAvailable: true };
  }
};

/**
 * Get remaining capacity from Apps Script
 */
export const getRemainingCapacity = async (): Promise<number> => {
  try {
    const status = await getRegistrationStatus();
    return status?.remaining ?? EVENT_CONSTANTS.MAX_CAPACITY;
  } catch (error) {
    console.error('Error getting capacity:', error);
    return EVENT_CONSTANTS.MAX_CAPACITY;
  }
};

/**
 * Get capacity percentage
 */
export const getCapacityPercentage = async (): Promise<number> => {
  try {
    const status = await getRegistrationStatus();
    if (status) {
      return Math.round(
        ((status.maxCapacity - status.remaining) / status.maxCapacity) * 100
      );
    }
    return 0;
  } catch (error) {
    console.error('Error getting capacity percentage:', error);
    return 0;
  }
};

/**
 * Resend verification email
 */
export const resendVerificationEmail = async (
  participantId: string
): Promise<RegistrationResponse> => {
  try {
    // Use FormData to avoid CORS issues
    const form = new FormData();
    form.append('data', JSON.stringify({
      action: 'resend',
      participantId: participantId,
    }));

    const response = await fetch(APPS_SCRIPT_URL, {
      method: 'POST',
      body: form,
    });

    const result = await response.json();
    return result;
  } catch (error) {
    console.error('Resend verification error:', error);
    return {
      success: false,
      message: 'Failed to resend verification email',
      error: 'RESEND_ERROR',
    };
  }
};
